/**
 * Status Tag Utilities
 *
 * Pure render functions for displaying invite and user status indicators.
 * Uses a small coloured dot followed by a label, matching the Figma design.
 * Keeps presentation logic out of page/organism components.
 *
 * @module features/users/utils/statusTags
 */
import React from 'react';
import type { InviteStatus } from '@/features/users/types/users.types';

/* ── Invite status dot colour mapping ─────────────── */
const INVITE_DOT_CLASS: Record<InviteStatus, string> = {
  PENDING: 'idf-status-dot--pending',
  EXPIRED: 'idf-status-dot--expired',
  REGISTERED: 'idf-status-dot--active',
};

const INVITE_LABEL: Record<InviteStatus, string> = {
  PENDING: 'Pending',
  EXPIRED: 'Expired',
  REGISTERED: 'Registered',
};

/**
 * Returns a localized dot-indicator for invite statuses.
 *
 * @param status - Invitation status (PENDING | EXPIRED | REGISTERED)
 * @param t - Translation function
 * @returns Dot + label element
 */
export const getInviteStatusTag = (
  status: InviteStatus,
  t: (key: string) => string
): React.ReactElement => {
  const dotClass = INVITE_DOT_CLASS[status] ?? 'idf-status-dot--active';
  const label = INVITE_LABEL[status] ?? 'Registered';

  return (
    <span className="idf-status-indicator">
      <span className={`idf-status-dot ${dotClass}`} />
      <span className="idf-status-label">{t(label)}</span>
    </span>
  );
};

/**
 * Returns a localized dot-indicator for user account statuses.
 *
 * @param status - User status string
 * @param t - Translation function
 * @returns Dot + label element
 */
export const getUserStatusTag = (
  status: string | undefined,
  t: (key: string) => string
): React.ReactElement => {
  const isInactive = status === 'INACTIVE';

  return (
    <span className="idf-status-indicator">
      <span
        className={`idf-status-dot ${isInactive ? 'idf-status-dot--inactive' : 'idf-status-dot--active'}`}
      />
      <span className="idf-status-label">
        {isInactive ? t('Inactive') : t('Active')}
      </span>
    </span>
  );
};
